import { useState, ReactNode, useCallback, FC, useEffect } from 'react';
import { Button } from 'antd';
import { useTranslation } from 'react-i18next';
import CircularStepsWithDropdown from '../ui-components/StepsDropdown';
import { useWizard } from './useWizard';
import StepsVertical from '../ui-components/StepsVertical';
import { UserRole } from '../../shared/VolunteerApplicationServiceTypes';
import { useApplicationId } from '../../hooks/useApplicationId';
import useUserSession from '../../hooks/useUserSession';

interface WizardProps {
	steps: Array<ReactNode>;
	activeStep: number;
	nextButtonText?: string;
	backButtonText?: string;
	cancelButtonText?: string;
	loading?: boolean;
	onNext: () => void;
	onPrevious: () => void;
	onSubmit: () => void;
	onCancel?: () => void;
	currentWizard?: number;
	isMobile?: boolean;
	navigateTo?: string;
}

export const Wizard: FC<WizardProps> = ({
	steps,
	activeStep,
	nextButtonText,
	backButtonText,
	loading = false,
	onNext,
	onPrevious,
	onSubmit,
	currentWizard,
	isMobile = false,
	navigateTo,
}) => {
	const { t } = useTranslation();
	const { selectedRole } = useUserSession();
	const isApplicantRole = selectedRole === UserRole.APPLICANT;

	const {
		nextButtonDisabled,
		submitButtonDisabled,
		cancelButtonDisabled,
		submitButtonText,
	} = useWizard();

	const isFirstWizard = currentWizard === 0;
	const { navigateWithApplicationId } = useApplicationId();
	const backBtnText =
		steps?.length <= 1 ? t('back') : backButtonText ?? t('previous');
	const submitBtnText = submitButtonText || t('submit');
	const nextBtnText = isApplicantRole
		? isFirstWizard && activeStep === 0
			? t('confirmAndContinue')
			: t('saveAndnext')
		: nextButtonText || t('next');

	const handleNavigateTo = useCallback(
		(navigatetoPage: string) => {
			navigateWithApplicationId(navigatetoPage);
		},
		[navigateWithApplicationId]
	);

	return (
		<div className={`flex flex-col sm:px-[8px]`}>
			<div className="h-full min-h-0">{steps[activeStep]}</div>
			<div
				className={`flex ${
					isMobile ? 'flex-col-reverse' : 'flex-row'
				} w-full h-fit gap-4 my-8`}
			>
				{/*Show the Previous Button based on the Steps and conditions also the Roles */}
				{isApplicantRole && (!isFirstWizard || activeStep > 0) && (
					<Button
						className="flex-1 min-h-[3rem] font-bold font-sans text-primary-blue border-primary-blue"
						onClick={onPrevious}
						disabled={cancelButtonDisabled}
					>
						{backBtnText}
					</Button>
				)}

				{/* Show 'Confirm and Continue' only for the first step of the first wizard */}
				{isFirstWizard && activeStep === 0 && steps?.length > 1 && (
					<Button
						className="flex-1 min-h-[3rem] font-bold font-sans bg-primary-blue !text-white"
						type="primary"
						disabled={nextButtonDisabled}
						loading={loading}
						onClick={onNext}
					>
						{nextBtnText}
					</Button>
				)}

				{/* Show 'Back' only for the one Step Wizards */}
				{isFirstWizard && activeStep === 0 && steps?.length == 1 && (
					<Button
						className="flex-1 min-h-[3rem] font-bold font-sans !text-primary-blue border-primary-blue"
						loading={loading}
						onClick={() => {
							if (navigateTo) {
								handleNavigateTo(navigateTo);
							}
						}}
					>
						{backBtnText}
					</Button>
				)}

				{/* Show 'Next' button if not the last step of any wizard */}
				{((!isFirstWizard && activeStep !== steps.length - 1) ||
					(activeStep < steps.length - 1 && activeStep !== 0)) && (
					<Button
						className="flex-1 min-h-[3rem] font-bold font-sans bg-primary-blue !text-white"
						type="primary"
						onClick={onNext}
						disabled={nextButtonDisabled}
						loading={loading}
					>
						{nextBtnText}
					</Button>
				)}

				{/* Only for last step of each wizard */}
				{activeStep === steps.length - 1 && (
					<Button
						className="flex-1 min-h-[3rem] font-bold font-sans bg-primary-blue !text-white"
						type="primary"
						disabled={submitButtonDisabled}
						loading={loading}
						onClick={onSubmit}
					>
						{submitBtnText}
					</Button>
				)}
			</div>
		</div>
	);
};

export const WizardContainer = ({
	steps,
	stepDescription,
	onSubmitClick,
	onCancelClick,
	onNextClick,
	onPreviousClick,
	stepOffset = 0,
	totalStepsInPreviousWizard = 0,
	currentWizard = 0,
	initialStepState = 0,
	onStepStateChange,
	draftIndex,
	completedDraftIndex,
	stepDisable,
	navigateTo,
	isProposerAndViewable,
}: {
	steps: Array<ReactNode>;
	stepDescription?: Array<{
		title: string;
		description?: string;
		progress?: number;
	}>;
	onSubmitClick: () => void;
	onCancelClick?: (stepIndex: number) => void;
	onNextClick?: (stepIndex: number) => void | Promise<boolean>;
	stepOffset?: number;
	totalStepsInPreviousWizard?: number;
	currentWizard?: number;
	onPreviousClick?: (wizardIndex: number) => void | Promise<boolean>;
	initialStepState?: number;
	onStepStateChange?: (step: number) => void;
	isFirstWizard?: boolean;
	draftIndex?: number;
	completedDraftIndex?: number;
	stepDisable?: boolean;
	navigateTo?: string;
	isProposerAndViewable?: boolean;
}) => {
	const [currentStep, setCurrentStep] = useState(initialStepState);

	const [isMobile, setIsMobile] = useState(window.innerWidth < 640);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 640);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		setCurrentStep(initialStepState);
	}, [initialStepState, currentWizard]);

	const globalStep = currentStep + stepOffset;

	const handleNext = useCallback(async () => {
		if (onNextClick) {
			let indexingStep = currentStep;
			if (currentWizard > 0) {
				indexingStep = totalStepsInPreviousWizard + currentStep;
			}
			const proceed = await onNextClick(indexingStep);
			if (!proceed) return;
		}

		setCurrentStep((prev) => prev + 1);
		window.scrollTo(0, 0);
	}, [currentStep, onNextClick, totalStepsInPreviousWizard, currentWizard]);

	const handlePrevious = useCallback(async () => {
		if (onPreviousClick) {
			if (stepOffset == 0 && currentStep === 0) {
				const previous = await onPreviousClick(currentWizard);
				if (!previous) return;
				setCurrentStep(totalStepsInPreviousWizard - 1);
				onStepStateChange?.(totalStepsInPreviousWizard - 1);
			} else {
				setCurrentStep((prev) => {
					const prevStep = prev - 1;
					onStepStateChange?.(prevStep);
					return prevStep;
				});
			}
		} else {
			setCurrentStep((prev) => prev - 1);
		}
		window.scrollTo(0, 0);
	}, [
		stepOffset,
		currentStep,
		totalStepsInPreviousWizard,
		onPreviousClick,
		currentWizard,
		onStepStateChange,
	]);

	const handleCancel = useCallback(() => {
		if (onCancelClick) {
			onCancelClick(globalStep);
		} else {
			setCurrentStep(0);
		}
	}, [globalStep, onCancelClick]);

	const handleStepChange = useCallback(
		(step: number) => {
			setCurrentStep(step);
			onStepStateChange?.(step);
		},
		[onStepStateChange]
	);

	// Dynamically calculate progress for each step
	const stepDecriptionWithProgress = stepDescription?.map((step, index) => ({
		...step,
		progress: ((index + 1) / steps.length) * 100,
	}));

	return (
		<>
			<div className="flex flex-col sm:flex-row gap-6 justify-center">
				{/* Steps for mobile */}
				{(steps?.length ?? 0) > 1 && (
					<div className="sm:hidden">
						<CircularStepsWithDropdown
							steps={stepDecriptionWithProgress ?? []}
							activeStep={currentStep}
							onNext={() => void handleNext()} // Wrap in void
							onPrevious={() => void handlePrevious()}
							onStepChange={handleStepChange}
							currentWizard={currentWizard}
							totalStepsInPreviousWizard={totalStepsInPreviousWizard}
							disableDropdown={!isProposerAndViewable ? stepDisable : false}
							draftIndex={draftIndex}
							completedDraftIndex={completedDraftIndex}
							isProposerAndViewable={isProposerAndViewable}
						/>
					</div>
				)}

				{/* Steps for desktop */}
				{(steps?.length ?? 0) > 1 && (
					<div className="hidden sm:block w-full max-w-md sm:w-fit py-4 px-6 bg-white rounded-lg h-min border border-solid border-gray-200">
						<StepsVertical
							steps={stepDecriptionWithProgress ?? []}
							activeStep={currentStep}
							onNext={() => void handleNext()}
							onPrevious={() => void handlePrevious()}
							onStepChange={handleStepChange}
							disableSteps={!isProposerAndViewable ? stepDisable : false}
							currentWizard={currentWizard}
							totalStepsInPreviousWizard={totalStepsInPreviousWizard}
							draftIndex={draftIndex}
							completedDraftIndex={completedDraftIndex}
							isProposerAndViewable={isProposerAndViewable}
						/>
					</div>
				)}

				{/* Form container */}
				<div className="w-full sm:w-fit sm:py-4 sm:px-4 sm:bg-white sm:rounded-lg border-0 sm:border sm:border-solid sm:border-gray-200">
					<Wizard
						steps={steps}
						activeStep={currentStep}
						onNext={() => void handleNext()}
						onPrevious={() => void handlePrevious()}
						onSubmit={onSubmitClick}
						onCancel={handleCancel}
						currentWizard={currentWizard}
						isMobile={isMobile}
						navigateTo={navigateTo}
					/>
				</div>
			</div>
		</>
	);
};
